/**
 * How free a Resource is over a Plan Line's months, in NOS HOURS — the one figure the
 * Resource picker ranks by (second, after Fit Score) and a line's Candidates show.
 *
 * DECIDED 2026-10-19 (Martyn), reversing 2026-10-17's points basis: the picker shows "hours
 * free" from NOS — capacity hours − guidance hours, the same figures as the plan grid's load
 * bars — and ranks by Fit Score first, then hours free (reserved-for-this-Account still on
 * top). So, per month, free = NOS capacity hours − NOS guidance hours, and the line's figure is:
 *
 *   - a Total plan: the SUM over the line's months from the current month on;
 *   - a Monthly plan: the AVERAGE over the next three of those months.
 *
 * Months already past are skipped (time that has gone is not free). NOS guidance only reaches
 * the current month + three, so a long line has months NOS has no figure for yet: they are
 * listed as `missingMonths` and contribute nothing, never a guessed zero.
 *
 * This is a display and ranking figure only. Capacity stays point-based everywhere else
 * (non-negotiable 1): nothing here converts hours into points or feeds Fit Score.
 * Being Suggested or a Candidate on another plan is NOT subtracted: neither books
 * anything. They are shown alongside as "also on N plans".
 *
 * Pure — the NOS read that feeds it is `getGuidance()` (src/api/guidance.ts).
 */

/** A plan's budget cadence decides whether the figure is a total or a monthly average. */
export type AvailabilityBasis = "Total" | "Monthly";

/** A plan's `budget_cadence` as the figure's basis: anything but Monthly is a Total. */
export function availabilityBasis(budgetCadence: string): AvailabilityBasis {
  return budgetCadence === "Monthly" ? "Monthly" : "Total";
}

/** How many months a Monthly plan's average looks ahead. */
export const MONTHLY_AVERAGE_MONTHS = 3;

/** One NOS month as the figure needs it (`YYYY-MM`). Hours null = NOS has no figure. */
export interface MonthHoursInput {
  month: string;
  capacityHours: number | null;
  guidanceHours: number | null;
}

export interface MonthHours {
  /** `YYYY-MM`. */
  month: string;
  capacityHours: number;
  guidanceHours: number;
  /** capacity − guidance. Negative = guided past capacity. */
  free: number;
}

export interface ResourceAvailability {
  basis: AvailabilityBasis;
  /** Sum (Total) or average (Monthly) of free hours over `months`; null when NOS has none. */
  freeHours: number | null;
  capacityHours: number | null;
  guidanceHours: number | null;
  /** The measured months NOS has figures for. */
  months: MonthHours[];
  /** Measured months NOS has no figure for yet (beyond its horizon, or no capacity). */
  missingMonths: string[];
}

/**
 * The months a line's figure is measured over (`YYYY-MM`): its months from `currentMonth` on,
 * and only the first three for a Monthly plan. A line wholly in the past measures nothing.
 */
export function measuredMonths(
  window: readonly string[],
  basis: AvailabilityBasis,
  currentMonth: string,
): string[] {
  const ahead = window.map((m) => m.slice(0, 7)).filter((m) => m >= currentMonth.slice(0, 7));
  return basis === "Monthly" ? ahead.slice(0, MONTHLY_AVERAGE_MONTHS) : ahead;
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

export function hoursAvailability(
  basis: AvailabilityBasis,
  measured: readonly string[],
  nos: readonly MonthHoursInput[],
): ResourceAvailability {
  const byMonth = new Map(nos.map((m) => [m.month.slice(0, 7), m]));
  const months: MonthHours[] = [];
  const missingMonths: string[] = [];
  for (const month of measured) {
    const m = byMonth.get(month);
    if (
      m === undefined ||
      m.capacityHours === null ||
      m.capacityHours <= 0 ||
      m.guidanceHours === null
    ) {
      missingMonths.push(month);
      continue;
    }
    months.push({
      month,
      capacityHours: m.capacityHours,
      guidanceHours: m.guidanceHours,
      free: round1(m.capacityHours - m.guidanceHours),
    });
  }
  if (months.length === 0) {
    return {
      basis,
      freeHours: null,
      capacityHours: null,
      guidanceHours: null,
      months,
      missingMonths,
    };
  }
  const sum = (pick: (m: MonthHours) => number) => months.reduce((t, m) => t + pick(m), 0);
  const scale = basis === "Monthly" ? 1 / months.length : 1;
  return {
    basis,
    freeHours: round1(sum((m) => m.free) * scale),
    capacityHours: round1(sum((m) => m.capacityHours) * scale),
    guidanceHours: round1(sum((m) => m.guidanceHours) * scale),
    months,
    missingMonths,
  };
}

/** "86 hrs" (Total) or "22 hrs/mo" (Monthly). */
export function hoursLabel(hours: number, basis: AvailabilityBasis): string {
  const n = Number.isInteger(hours) ? String(hours) : hours.toFixed(1);
  return `${n} hr${hours === 1 ? "" : "s"}${basis === "Monthly" ? "/mo" : ""}`;
}

/** `2026-01` → `Jan 2026`. */
export function monthLabel(month: string): string {
  const [y, m] = month.split("-");
  const names = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const name = names[Number(m) - 1];
  return name === undefined || y === undefined ? month : `${name} ${y}`;
}

/** "Jan–Jun 2026", "Nov 2026–Feb 2027", or the single month. */
export function windowLabel(months: readonly string[]): string {
  const first = months[0];
  const last = months[months.length - 1];
  if (first === undefined || last === undefined) return "the plan's months";
  if (first === last) return monthLabel(first);
  const [fy] = first.split("-");
  const [ly] = last.split("-");
  const a = monthLabel(first);
  const b = monthLabel(last);
  return fy === ly ? `${a.slice(0, 3)}–${b}` : `${a}–${b}`;
}

/**
 * RP-33.4 as shipped: a deterministic one-line reason for a top-ranked Resource, built ONLY
 * from figures already on the row — no model call, no hidden input (spec §11 keeps an LLM
 * explanation at Stage 3).
 */
export function rankReason(input: {
  availability: ResourceAvailability;
  rating: number | null;
  ratingRange: { min: number; max: number } | null;
  otherPlanCount: number;
}): string {
  const { availability: a } = input;
  const parts: string[] = [];
  const span = windowLabel(a.months.map((m) => m.month));
  const per = a.basis === "Monthly" ? " a month" : "";
  if (a.freeHours === null) {
    parts.push("no NOS hours for these months yet");
  } else if (a.freeHours > 0) {
    parts.push(`${hoursLabel(a.freeHours, "Total")} free${per} across ${span}`);
  } else if (a.freeHours === 0) {
    parts.push(`fully guided across ${span}`);
  } else {
    parts.push(`guided ${hoursLabel(-a.freeHours, "Total")} past capacity${per} across ${span}`);
  }
  if (input.rating !== null) {
    parts.push(
      input.ratingRange === null
        ? `rating ${input.rating}`
        : `rating ${input.rating} vs ${input.ratingRange.min}–${input.ratingRange.max}`,
    );
  }
  if (input.otherPlanCount > 0) {
    parts.push(`on ${input.otherPlanCount} other plan${input.otherPlanCount === 1 ? "" : "s"}`);
  }
  const text = parts.join(" · ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** One Resource's place in the picker's order. Null = no figure (gated, unscored, no NOS). */
export interface PickerRank {
  /** 0 = reserved for this Account, 1 = unreserved, 2 = reserved elsewhere. */
  bucket: number;
  fit: number | null;
  hours: number | null;
  name: string;
  id: string;
}

/** Higher first; an unknown figure (null) sorts after every known one. */
function compareDesc(a: number | null, b: number | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return b - a;
}

/**
 * The picker's order (decision 2026-10-19): reserved for this Account first, unreserved next,
 * reserved elsewhere last; within each, Fit Score, then NOS hours free, then name.
 */
export function comparePickerRank(a: PickerRank, b: PickerRank): number {
  return (
    a.bucket - b.bucket ||
    compareDesc(a.fit, b.fit) ||
    compareDesc(a.hours, b.hours) ||
    (a.name < b.name ? -1 : a.name > b.name ? 1 : 0) ||
    (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
  );
}

export type RatingMismatch = "below" | "above" | "unrated" | null;

/**
 * RP-33.1 — how a Resource sits against the line's expected Discipline and rating range.
 * A missing or unconfirmed rating is "unrated" here, never 1: treating it as 1 is a SCORING
 * rule (Fit Score), not a filtering one.
 */
export function ratingMismatch(
  rating: number | null,
  range: { min: number; max: number } | null,
): RatingMismatch {
  if (range === null) return null;
  if (rating === null) return "unrated";
  if (rating < range.min) return "below";
  if (rating > range.max) return "above";
  return null;
}
